import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';

// Define the GA4 measurement ID (must match the one in AnalyticsContext)
const GA4_MEASUREMENT_ID = 'G-286QCF41P7';

/**
 * Hook to track page views automatically
 * Uses the current location from react-router-dom to track page views
 * Ensures client ID is preserved across page navigations
 */
export const usePageTracking = () => {
  const location = useLocation();
  const { hasConsent } = useAnalytics();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Only track if user has given consent and gtag is loaded
    if (!hasConsent || !window.gtag) return;

    // Extract a clean path without locale prefix for tracking
    const path = location.pathname;
    const cleanPath = path.replace(/^\/(en|pt-BR)\//, '/');

    // Don't track the same path twice in a row (prevents duplicate events on remounts)
    if (previousPath.current === cleanPath) return;
    previousPath.current = cleanPath;

    // Use a small timeout to ensure GA4 is fully initialized
    // This helps with lazy-loaded components
    const trackingTimeout = setTimeout(() => {
      // Track the page view directly with gtag to ensure client ID persistence
      window.gtag('event', 'page_view', {
        page_path: cleanPath,
        page_title: document.title,
        page_location: window.location.href,
        send_to: GA4_MEASUREMENT_ID,
        non_interaction: false // Count as an interaction
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('GA4 page view tracked:', cleanPath);
      }
    }, 10);

    // Clean up timeout if component unmounts
    return () => clearTimeout(trackingTimeout);
  }, [location, hasConsent]);
};
